import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Plus} from '../../icons';
import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {YesNoModal} from '../../elements/YesNoModal';
import {ANALYTICS_CHANNEL, ESCALAFY_CHANNEL, MERCADO_ADS_CHANNEL} from '../../channels/channels';
import {MercadoAdsConnectNotice} from '../../channels/mercado_ads/MercadoAdsConnectNotice';
import {ShopifyLinkRequestsNotice} from '../../channels/shopify/ShopifyLinkRequestsNotice';
import {useShopifyInstallByLink} from '../../../context/UserModulesContext';
import {SendConnectionLinkModal} from '../../connection_links/SendConnectionLinkModal';
import {LocalStorage} from '../../../utils/LocalStorage';
import {AgencyHeader} from '../AgencyHeader';
import {AgencyTabs} from '../AgencyTabs';
import {ConnectionsSubTabs, CONNECTIONS_TAB_PLATFORMS} from '../ConnectionsSubTabs';
import {WebhookComponent} from '../webhook/WebhookComponent';
import {CreateControlLinesModal} from '../link_acocunts/CreateControlLinesModal';
import {FacebookReconnectModal} from '../connections/FacebookReconnectModal';
import {ReconnectAdAccountModal} from '../connections/ReconnectAdAccountModal';
import {AddClientAdAccountModal} from '../../client/ad_accounts/AddClientAdAccountModal';
import {reconnectCredential} from '../connections/reconnectCredential';
import {usePlatformsConnections} from './usePlatformsConnections';
import {
    FAMILIES, addChannelFrom, attentionOf, credentialToExpandFrom, discoveryOf, familiesOf, initiallyOpenChannels,
    platformsOf,
    readHiddenChannels, splitDiscovery, writeHiddenChannels,
} from './platformsModel';
import {platformName} from './platformsText';
import {AttentionNotice} from './AttentionNotice';
import {DiscoverBlock, ShowHiddenLink} from './DiscoverBlock';
import {PlatformRow} from './PlatformRow';
import {PlatformAddFlows} from './PlatformAddFlows';
import {ActionsMenu} from './ActionsMenu';
import {PlatformLogo} from './PlatformLogo';
import {Ga4AttributionRulesCard} from './attribution/Ga4AttributionRulesCard';

const NS = 'agency_v2.platforms';

/**
 * Conexiones › Plataformas en la UX 2.1 (tanda «Agencia 2.1», D6/D7/D11/D12, contrato §2).
 *
 * En este orden: el aviso de lo que necesita atención; «Tus conexiones», con las familias (Paid media ·
 * Analítica · Email marketing · Tiendas · Rentabilidad) y cada plataforma plegable con su estado (debajo
 * de la fila de GA4, «Cómo GA4 identifica tu pauta», la regla de cruce por plataforma: `attribution/`,
 * UD-10); «Dashbo también se conecta con estas plataformas», con las que la agencia todavía no usa; y al pie el
 * webhook de siempre en una tarjeta, hasta que la pantalla nueva de Alertas lo tenga en producción (D11).
 * Lo propio va arriba porque reconectar es la tarea frecuente (recorrido con Pablo, 13/10, C-09); sin
 * ninguna conexión, el catálogo queda primero.
 *
 * Los datos y las escrituras son los de la pantalla clásica (`usePlatformsConnections`); lo que se
 * decide sobre ellos vive en `platformsModel.js`. El clásico (`AgencyConnectionsConfigurationContent`)
 * no se tocó: la bifurcación está en la ruta (`AgencyConnectionsConfiguration`).
 */
export function PlatformsV2({location, navigate}) {
    const {t} = useTranslation();
    const data = usePlatformsConnections({
        search: location && location.search,
        pathname: location && location.pathname,
        navigate,
    });
    const {connections} = data;

    const agencyId = LocalStorage.fetchAgencyId();
    const userId = LocalStorage.fetchUserId();
    const [hiddenChannels, setHiddenChannels] = useState(() => readHiddenChannels(agencyId, userId));

    const platforms = useMemo(() => platformsOf(connections), [connections]);
    const families = useMemo(() => familiesOf(platforms), [platforms]);
    const attention = useMemo(() => attentionOf(platforms), [platforms]);
    const discovery = useMemo(
        () => splitDiscovery(discoveryOf(platforms), hiddenChannels),
        [platforms, hiddenChannels],
    );

    // Abiertas al llegar: las que piden algo. Se decide con la PRIMERA carga; un refresco de fondo
    // no le cierra al usuario lo que abrió ni le abre lo que cerró.
    // Al volver de conectar, además, la plataforma y el acceso que se conectó, con sus cuentas a la
    // vista: es lo primero que hay que poder mirar (cc#205). Se lee una vez, como los demás params.
    const [justConnected] = useState(() => credentialToExpandFrom(location && location.search));
    const [openChannels, setOpenChannels] = useState(null);
    useEffect(() => {
        if (!connections || openChannels !== null) return;
        const initial = initiallyOpenChannels(platforms);
        if (justConnected && !initial.includes(justConnected.channel)) initial.push(justConnected.channel);
        setOpenChannels(initial);
    }, [connections, platforms, openChannels, justConnected]);
    const toggle = (channel) => setOpenChannels((current) => {
        const list = current || [];
        return list.includes(channel) ? list.filter((each) => each !== channel) : [...list, channel];
    });

    const addFlows = useRef(null);
    const addChannels = platforms.filter((platform) => !platform.legacyGa4).map((platform) => platform.channel);
    const openAdd = useCallback((channel) => {
        if (addFlows.current) addFlows.current.open(channel);
    }, []);

    // `?add=<CANAL>`: llega de un modal Vincular cuenta («Conectar … con tu usuario», cc#209, o el
    // estado vacío, UD-9) y abre el alta de ese acceso. Una sola vez, con las conexiones ya cargadas (el alta necesita saber qué
    // canales hay), y se limpia la URL para que un F5 no la reabra.
    const [addFromUrl] = useState(() => addChannelFrom(location && location.search));
    const addFromUrlDone = useRef(false);
    useEffect(() => {
        if (!addFromUrl || addFromUrlDone.current || !connections) return;
        addFromUrlDone.current = true;
        if (addChannels.includes(addFromUrl)) openAdd(addFromUrl);
        window.history.replaceState({}, '', (location && location.pathname) || '/agency/conections');
    }, [addFromUrl, connections, addChannels, openAdd, location]);

    // A dónde va el foco después de ocultar o devolver tarjetas: lo que tenía el foco se desmonta en
    // los dos casos (la última tarjeta se lleva el bloque; «Ver las N que ocultaste» se va con su clic).
    const [focusAfter, setFocusAfter] = useState(null);
    const clearFocusAfter = useCallback(() => setFocusAfter(null), []);

    const hide = (channel) => {
        const next = Array.from(new Set([...hiddenChannels, channel]));
        setHiddenChannels(next);
        writeHiddenChannels(agencyId, userId, next);
        if (discovery.visible.every((platform) => platform.channel === channel)) setFocusAfter('show-hidden');
    };
    const showHidden = () => {
        setHiddenChannels([]);
        writeHiddenChannels(agencyId, userId, []);
        setFocusAfter('discover');
    };

    // --- Acciones de las filas ------------------------------------------------------------------
    const [confirm, setConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [clientForStore, setClientForStore] = useState(null);
    const [reauthorizing, setReauthorizing] = useState(null);
    // «Enviar link al vendedor» de Mercado Ads: `null` cerrado; `{agencyClientId}` abierto (sin cliente,
    // el modal lo pide). Con un acceso cortado que conectó el cliente, va con ese cliente ya elegido.
    const [mercadoAdsLink, setMercadoAdsLink] = useState(null);
    const connectClientModal = useRef(null);
    const {enabled: shopifyByLink} = useShopifyInstallByLink();

    const reconnect = useCallback((channel, credential) => (
        reconnectCredential(channel, credential, {onFacebook: data.openFacebookReconnect}).then((result) => {
            if (!result.ok) toast.error(t(result.errorKey || `${NS}.reconnect_error`));
            return result;
        })
    ), [data.openFacebookReconnect, t]);

    // Si la escritura falla, la confirmación queda abierta (con el aviso del error) para reintentar.
    const confirmYes = () => {
        if (!confirm || saving) return;
        setSaving(true);
        const done = confirm.type === 'store'
            ? data.removeStore(confirm.store.ad_account_id)
            : data.removeCredential(confirm.channel, confirm.credential.id);
        done.then((ok) => {
            setSaving(false);
            if (ok) setConfirm(null);
        });
    };

    // Reactstrap sigue dibujando el contenido mientras el modal se va: con el estado ya en `null`, el
    // título y el texto desaparecían un instante antes que la caja. Se dibuja el último que hubo.
    const lastConfirm = useRef(null);
    if (confirm) lastConfirm.current = confirm;
    const shownConfirm = confirm || lastConfirm.current;
    const lastReauthorizing = useRef(null);
    if (reauthorizing) lastReauthorizing.current = reauthorizing;

    // `AddClientAdAccountModal` es de los de ref + `toggle()`, y su X y Escape lo cierran sin avisarle
    // al padre. Por eso cada «Conectar a un cliente» es una apertura NUEVA (un número que no se repite,
    // que además es la `key` del modal): con la tienda sola, reabrir la misma después de cerrarla sin
    // conectar dejaba el mismo estado, el efecto no corría y el link no hacía nada.
    const openings = useRef(0);
    useEffect(() => {
        if (clientForStore && connectClientModal.current) connectClientModal.current.toggle();
    }, [clientForStore]);

    const actions = {
        renameCredential: data.renameCredential,
        reconnect,
        confirmRemoveCredential: (channel, credential) => setConfirm({type: 'credential', channel, credential}),
        confirmRemoveStore: (store) => setConfirm({type: 'store', store}),
        connectStoreToClient: (store) => {
            openings.current += 1;
            setClientForStore({store, opening: openings.current});
        },
        reauthorizeStore: (store) => setReauthorizing(store),
        add: openAdd,
        // Con Shopify por link de instalación, «Mandarle el link al cliente» (los códigos DSH- de la app pública)
        // no existe: su reemplazo es «Copiar link para tu cliente», en el aviso de los pedidos.
        sendShopifyLink: shopifyByLink ? null : () => addFlows.current && addFlows.current.sendShopifyLink(),
        sendMercadoAdsLink: (credential) => setMercadoAdsLink({
            agencyClientId: (credential && (credential.managed_by_client_id || credential.agency_client_id)) || null,
        }),
    };

    const confirmTexts = !shownConfirm ? {} : shownConfirm.type === 'store'
        ? {
            title: t('ecommerce_connection_row.remove_title'),
            message: t('ecommerce_connection_row.remove_message'),
            question: t('ecommerce_connection_row.remove_question'),
        }
        : {
            title: t('credential_row.delete_title'),
            message: t(shownConfirm.channel === ESCALAFY_CHANNEL
                ? 'agency_connections.escalafy_delete_confirm'
                : 'credential_row.delete_message'),
            question: t('credential_row.delete_question'),
        };

    const connectMenuItems = [];
    FAMILIES.forEach((family) => {
        const inFamily = platforms.filter((platform) => platform.family === family.key && !platform.legacyGa4);
        if (!inFamily.length) return;
        connectMenuItems.push({key: `h-${family.key}`, heading: t(`${NS}.family.${family.key}`)});
        inFamily.forEach((platform) => connectMenuItems.push({
            key: platform.channel,
            label: platformName(platform.channel),
            icon: <PlatformLogo channel={platform.channel} size={18} className="-xs"/>,
            onSelect: () => openAdd(platform.channel),
        }));
    });

    const connectCta = connections && connectMenuItems.length > 0 && (
        <ActionsMenu label={t(`${NS}.connect_platform`)} items={connectMenuItems}
                     triggerClassName="apl-btn -primary" menuClassName="-wide"
                     triggerContent={<><Plus size={16} weight="bold" aria-hidden="true"/>{t(`${NS}.connect_platform`)}</>}/>
    );

    const renderBody = () => {
        if (data.loadError !== null) {
            return (
                <LoadErrorState title={t('agency_connections_configuration.load_error')}
                                text={t('agency_connections_configuration.load_error_explanation')}
                                retryLabel={t('agency_connections_configuration.load_error_retry')}
                                onRetry={data.retry}/>
            );
        }
        if (data.loading) {
            return (
                <div className="box-loader">
                    <Spinner color="positive"/>
                </div>
            );
        }
        return (
            <>
                {/* La vuelta de conectar Mercado Ads: lo que se conectó, o el error con qué hacer. */}
                <MercadoAdsConnectNotice/>
                {/* Los pedidos de link de instalación de Shopify (con el flag). A nivel de pantalla: la fila de
                    Shopify sólo se dibuja con una tienda conectada, y el primer pedido no tendría dónde verse. */}
                <ShopifyLinkRequestsNotice/>
                <AttentionNotice items={attention} onReconnect={reconnect} onSendLink={actions.sendMercadoAdsLink}/>
                {families.length > 0 && (
                    <section className="apl-yours" aria-labelledby="apl-yours-title" data-testid="apl-yours">
                        <h2 className="apl-yours__title" id="apl-yours-title">{t(`${NS}.yours_title`)}</h2>
                        {families.map((family) => (
                            <section key={family.key} className="apl-family" aria-labelledby={`apl-family-${family.key}`}
                                     data-testid={`apl-family-${family.key}`}>
                                <div className="apl-family__head">
                                    <h3 className="apl-section-title" id={`apl-family-${family.key}`}>{t(`${NS}.family.${family.key}`)}</h3>
                                    <span className="apl-family__count">{t(`${NS}.family_connected`, {count: family.platforms.length})}</span>
                                </div>
                                <div className="apl-card">
                                    {family.platforms.map((platform) => (
                                        <React.Fragment key={platform.channel}>
                                            <PlatformRow platform={platform}
                                                         open={(openChannels || []).includes(platform.channel)}
                                                         onToggle={() => toggle(platform.channel)} actions={actions}
                                                         expandCredentialId={justConnected && justConnected.channel === platform.channel
                                                             ? justConnected.credentialId : null}/>
                                            {/* «Cómo GA4 identifica tu pauta» (UD-10): la regla de cruce vive en la
                                                conexión de GA4. Pide y decide sola si se dibuja (sin GA4 conectada,
                                                sin cuentas vinculadas o con un backend viejo, nada). */}
                                            {platform.channel === ANALYTICS_CHANNEL && <Ga4AttributionRulesCard/>}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </section>
                )}
                <DiscoverBlock platforms={discovery.visible} hiddenCount={discovery.hidden.length}
                               onConnect={openAdd} onHide={hide} onShowHidden={showHidden}
                               focusNow={focusAfter === 'discover'} onFocused={clearFocusAfter}/>
                {discovery.visible.length === 0 && (
                    <ShowHiddenLink count={discovery.hidden.length} onShowHidden={showHidden} standalone
                                    focusNow={focusAfter === 'show-hidden'} onFocused={clearFocusAfter}/>
                )}
            </>
        );
    };

    return (
        <section className="section-content">
            <AgencyHeader/>
            <AgencyTabs activeTab="-sixth"/>
            <ConnectionsSubTabs activeSubTab={CONNECTIONS_TAB_PLATFORMS} actions={connectCta || null}/>
            <div className="section-body apl-screen" data-testid="apl-screen">
                {renderBody()}
                {/* El webhook sigue acá hasta que la pantalla nueva de Alertas lo tenga en producción
                    (D11): ahí se reemplaza por la línea `webhook_moved`, en un commit aparte. */}
                <section className="apl-webhook" aria-labelledby="apl-webhook-title">
                    <h2 className="apl-section-title" id="apl-webhook-title">{t(`${NS}.webhook_card_title`)}</h2>
                    <div className="apl-card apl-webhook__body">
                        <WebhookComponent/>
                    </div>
                </section>
            </div>

            {connections && (
                <PlatformAddFlows ref={addFlows} channels={addChannels}
                                  onStoreConnected={() => {
                                      toast.success(t(`${NS}.store_connected`));
                                      data.refresh();
                                  }}/>
            )}
            <YesNoModal skipRenderButton isOpen={confirm !== null} isSaving={saving}
                        title={confirmTexts.title} message={confirmTexts.message} question={confirmTexts.question}
                        onYesAnswer={confirmYes} onNoAnswer={() => setConfirm(null)}/>
            {clientForStore && (
                <AddClientAdAccountModal key={clientForStore.opening} ref={connectClientModal}
                                         channel={clientForStore.store.channel}
                                         adAccountId={clientForStore.store.account_id}
                                         afterSuccessfulSubmit={() => {
                                             setClientForStore(null);
                                             data.refresh();
                                         }}/>
            )}
            <ReconnectAdAccountModal isOpen={reauthorizing !== null}
                                     adAccountData={reauthorizing || lastReauthorizing.current}
                                     onToggle={() => setReauthorizing(null)}
                                     onReconnected={() => {
                                         setReauthorizing(null);
                                         data.refresh();
                                     }}/>
            <CreateControlLinesModal isOpen={data.controlLines.open}
                                     toggle={() => data.closeControlLines()}
                                     adAccounts={data.controlLines.adAccounts}
                                     onFinish={data.closeControlLines}/>
            <SendConnectionLinkModal show={mercadoAdsLink !== null} onHide={() => setMercadoAdsLink(null)}
                                     channel={MERCADO_ADS_CHANNEL}
                                     agencyClientId={(mercadoAdsLink && mercadoAdsLink.agencyClientId) || undefined}/>
            <FacebookReconnectModal isOpen={data.facebookReconnect.open}
                                    reconnectAccId={data.facebookReconnect.accId}
                                    onClose={data.closeFacebookReconnect}/>
        </section>
    );
}

export default PlatformsV2;
